import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shuffle } from 'lucide-react';
import { CopyTextButton } from './CopyTextButton';
import { EntityThumb } from './EntityThumb';
import { coordLabel, formatSeconds, isPoint, locationCopyText, mapName, monsterName, spotPath } from '../../utils/worldFormat';
import { selectionNote } from '../../utils/worldQueries';
import type { Spawn, WorldData } from '../../types/world';
type Props = {
  w: WorldData;
  s: Spawn;
  show?: 'monster' | 'map' | 'both';
};

/** Un registro de spawns.csv. Nunca se fusiona con otros aunque compartan monstruo o coordenadas. */
export function SpawnRow({
  w,
  s,
  show = 'both'
}: Props) {
  const mon = monsterName(w, s.monsterId);
  const map = mapName(w, s.mapId);
  const title = show === 'map' ? map : show === 'monster' ? mon : `${mon} · ${map}`;
  const coords = coordLabel(s);
  const random = s.mapSelection === 'random';
  const level = w.monsters.get(s.monsterId)?.level;
  return <li className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0A1628] pl-3 pr-2 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1758" data-mp="r1b1c1vudurt">
      {/* Imagen del monstruo fuera del enlace: tocarla no abre el spot. */}
      {show !== 'map' && <EntityThumb entity={{
      type: 'monster',
      id: s.monsterId
    }} name={mon} data-id="element-1759" data-mp="2rtr0kpx4natp" />}
      <Link to={spotPath(s.id)} className="flex min-w-0 flex-1 items-center gap-3 py-3.5" data-id="element-1760" data-mp="2wstrl6g9vgio">
        <span className="min-w-0 flex-1" data-id="element-1761" data-mp="2itaxhhkaq5pd">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-1762" data-mp="2isj0q3eyq1xi">
            <span className="font-semibold text-white" data-id="element-1763" data-mp="2ntykls5em2eb">{title}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.kind === 'invasion' ? 'bg-[#D4AF37]/15 text-[#F3E5AB]' : 'bg-white/[0.06] text-gray-300'}`} data-id="element-1764" data-mp="28vwi7la3zmyn">
              {s.kind === 'invasion' ? 'Invasión' : 'Spot'}
            </span>
            {show !== 'map' && level !== undefined && <span className="text-xs text-gray-500" data-id="element-1765" data-mp="c34eoyz4q4sm">Nv. monstruo {level}</span>}
          </span>
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-400" data-id="element-1766" data-mp="alxp9w1u7kj5">
            <span className="font-mono text-[13px] text-gray-200" data-id="element-1767" data-mp="5n489tncdbe8">{coords ?? 'Coordenadas no documentadas'}</span>
            {coords && <span className="text-gray-500" data-id="element-1768" data-mp="nhyxvamwfap">{isPoint(s) ? 'Punto' : 'Área'}</span>}
            {s.quantity !== undefined && <span data-id="element-1769" data-mp="3ssvjz8e0sctz">Cantidad: {s.quantity}</span>}
            {s.respawnSeconds !== undefined && <span data-id="element-1770" data-mp="td8abwkvt334">Reaparece: {formatSeconds(s.respawnSeconds)}</span>}
          </span>
          {random && <span className="mt-1 flex items-center gap-1.5 text-xs text-[#D4AF37]/90" data-id="element-1771" data-mp="odc7w1ru1jpo">
              <Shuffle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1772" data-mp="jmdminrzlvfk" />
              {selectionNote(s)}
            </span>}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1773" data-mp="fj7mhxvgx0hj" />
      </Link>
      {coords && <CopyTextButton iconOnly text={locationCopyText(map, mon, s)} label="Copiar ubicación" data-id="element-1774" data-mp="1c5161yrc52ej" />}
    </li>;
}